import { createSlice } from "@reduxjs/toolkit";
import {
  getSalesRecapThunk,
  createSalesRecapThunk,
  updateSalesRecapThunk,
  deleteSalesRecapThunk,
  calculateRevenueShareThunk,
  createInvoiceFromRecapThunk,
  getAllSalesRecapSchedulesThunk,
  submitSalesRecapDataThunk,
  finalizeSalesRecapDataThunk,
  deleteSalesRecapScheduleThunk,
} from "state/action/sales_recap";

const asyncState = (overrides = {}) => ({
  data: null,
  isFulfilled: false,
  isPending: false,
  isRejected: false,
  err: null,
  ...overrides,
});

const initialState = {
  getSalesRecap: asyncState(),
  createSalesRecap: asyncState(),
  updateSalesRecap: asyncState(),
  deleteSalesRecap: asyncState(),
  calculateResult: asyncState(),
  createInvoiceResult: asyncState(),
  // Schedule (contract_sales_recap_schedule)
  getSchedules: asyncState(),
  submitSchedule: asyncState(),
  finalizeSchedule: asyncState(),
  deleteSchedule: asyncState(),
  // Form state untuk create/edit recap
  recapForm: {
    pks_id: "",
    mitra: "",
    period_start: null,
    period_end: null,
    items: [{ sale_date: null, description: "", gross_amount: "", net_amount: "" }],
    notes: "",
  },
  // Recap yang sedang diselect (untuk detail view)
  selectedRecap: null,
  // Preview kalkulasi
  calcPreview: null,
};

const salesRecapSlice = createSlice({
  name: "salesRecap",
  initialState,
  reducers: {
    // ── Form setters ──────────────────────────────────────────────────────────
    setRecapForm: (state, action) => {
      state.recapForm = { ...state.recapForm, ...action.payload };
    },
    resetRecapForm: (state) => {
      state.recapForm = initialState.recapForm;
      state.calcPreview = null;
    },
    setSelectedRecap: (state, action) => {
      state.selectedRecap = action.payload;
    },
    setCalcPreview: (state, action) => {
      state.calcPreview = action.payload;
    },
    clearCalcPreview: (state) => {
      state.calcPreview = null;
    },
    // ── Item management dalam form ────────────────────────────────────────────
    addRecapItem: (state) => {
      state.recapForm.items.push({
        sale_date: null,
        description: "",
        gross_amount: "",
        net_amount: "",
      });
    },
    removeRecapItem: (state, action) => {
      if (state.recapForm.items.length > 1) {
        state.recapForm.items.splice(action.payload, 1);
      }
    },
    updateRecapItem: (state, action) => {
      const { index, field, value } = action.payload;
      if (state.recapForm.items[index]) {
        state.recapForm.items[index][field] = value;
      }
    },
  },
  extraReducers(builder) {
    // ── GET ───────────────────────────────────────────────────────────────────
    builder
      .addCase(getSalesRecapThunk.pending, (state) => {
        state.getSalesRecap = asyncState({ isPending: true });
      })
      .addCase(getSalesRecapThunk.fulfilled, (state, action) => {
        state.getSalesRecap = asyncState({ isFulfilled: true, data: action.payload });
      })
      .addCase(getSalesRecapThunk.rejected, (state, action) => {
        state.getSalesRecap = asyncState({ isRejected: true, err: action.error.message });
      });

    // ── CREATE ────────────────────────────────────────────────────────────────
    builder
      .addCase(createSalesRecapThunk.pending, (state) => {
        state.createSalesRecap = asyncState({ isPending: true });
      })
      .addCase(createSalesRecapThunk.fulfilled, (state, action) => {
        state.createSalesRecap = asyncState({ isFulfilled: true, data: action.payload });
      })
      .addCase(createSalesRecapThunk.rejected, (state, action) => {
        state.createSalesRecap = asyncState({ isRejected: true, err: action.error.message });
      });

    // ── UPDATE ────────────────────────────────────────────────────────────────
    builder
      .addCase(updateSalesRecapThunk.pending, (state) => {
        state.updateSalesRecap = asyncState({ isPending: true });
      })
      .addCase(updateSalesRecapThunk.fulfilled, (state, action) => {
        state.updateSalesRecap = asyncState({ isFulfilled: true, data: action.payload });
        if (state.selectedRecap?._id === action.payload?.data?._id) {
          state.selectedRecap = action.payload.data;
        }
      })
      .addCase(updateSalesRecapThunk.rejected, (state, action) => {
        state.updateSalesRecap = asyncState({ isRejected: true, err: action.error.message });
      });

    // ── DELETE ────────────────────────────────────────────────────────────────
    builder
      .addCase(deleteSalesRecapThunk.pending, (state) => {
        state.deleteSalesRecap = asyncState({ isPending: true });
      })
      .addCase(deleteSalesRecapThunk.fulfilled, (state) => {
        state.deleteSalesRecap = asyncState({ isFulfilled: true });
      })
      .addCase(deleteSalesRecapThunk.rejected, (state, action) => {
        state.deleteSalesRecap = asyncState({ isRejected: true, err: action.error.message });
      });

    // ── CALCULATE ─────────────────────────────────────────────────────────────
    builder
      .addCase(calculateRevenueShareThunk.pending, (state) => {
        state.calculateResult = asyncState({ isPending: true });
      })
      .addCase(calculateRevenueShareThunk.fulfilled, (state, action) => {
        state.calculateResult = asyncState({ isFulfilled: true, data: action.payload });
        state.calcPreview = action.payload?.data ?? null;
      })
      .addCase(calculateRevenueShareThunk.rejected, (state, action) => {
        state.calculateResult = asyncState({ isRejected: true, err: action.error.message });
      });

    // ── CREATE INVOICE FROM RECAP ─────────────────────────────────────────────
    builder
      .addCase(createInvoiceFromRecapThunk.pending, (state) => {
        state.createInvoiceResult = asyncState({ isPending: true });
      })
      .addCase(createInvoiceFromRecapThunk.fulfilled, (state, action) => {
        state.createInvoiceResult = asyncState({ isFulfilled: true, data: action.payload });
      })
      .addCase(createInvoiceFromRecapThunk.rejected, (state, action) => {
        state.createInvoiceResult = asyncState({ isRejected: true, err: action.error.message });
      });

    // ── GET SCHEDULES (contract_sales_recap_schedule) ─────────────────────────
    builder
      .addCase(getAllSalesRecapSchedulesThunk.pending, (state) => {
        state.getSchedules = asyncState({ isPending: true });
      })
      .addCase(getAllSalesRecapSchedulesThunk.fulfilled, (state, action) => {
        state.getSchedules = asyncState({ isFulfilled: true, data: action.payload });
      })
      .addCase(getAllSalesRecapSchedulesThunk.rejected, (state, action) => {
        state.getSchedules = asyncState({ isRejected: true, err: action.error.message });
      });

    // ── SUBMIT SCHEDULE ───────────────────────────────────────────────────────
    builder
      .addCase(submitSalesRecapDataThunk.pending, (state) => {
        state.submitSchedule = asyncState({ isPending: true });
      })
      .addCase(submitSalesRecapDataThunk.fulfilled, (state, action) => {
        state.submitSchedule = asyncState({ isFulfilled: true, data: action.payload });
      })
      .addCase(submitSalesRecapDataThunk.rejected, (state, action) => {
        state.submitSchedule = asyncState({ isRejected: true, err: action.error.message });
      });

    // ── FINALIZE SCHEDULE ─────────────────────────────────────────────────────
    builder
      .addCase(finalizeSalesRecapDataThunk.pending, (state) => {
        state.finalizeSchedule = asyncState({ isPending: true });
      })
      .addCase(finalizeSalesRecapDataThunk.fulfilled, (state, action) => {
        state.finalizeSchedule = asyncState({ isFulfilled: true, data: action.payload });
      })
      .addCase(finalizeSalesRecapDataThunk.rejected, (state, action) => {
        state.finalizeSchedule = asyncState({ isRejected: true, err: action.error.message });
      });

    // ── DELETE SCHEDULE ───────────────────────────────────────────────────────
    builder
      .addCase(deleteSalesRecapScheduleThunk.pending, (state) => {
        state.deleteSchedule = asyncState({ isPending: true });
      })
      .addCase(deleteSalesRecapScheduleThunk.fulfilled, (state) => {
        state.deleteSchedule = asyncState({ isFulfilled: true });
      })
      .addCase(deleteSalesRecapScheduleThunk.rejected, (state, action) => {
        state.deleteSchedule = asyncState({ isRejected: true, err: action.error.message });
      });
  },
});

export const salesRecapAction = {
  ...salesRecapSlice.actions,
  getSalesRecapThunk,
  createSalesRecapThunk,
  updateSalesRecapThunk,
  deleteSalesRecapThunk,
  calculateRevenueShareThunk,
  createInvoiceFromRecapThunk,
  // Schedule thunks
  getAllSalesRecapSchedulesThunk,
  submitSalesRecapDataThunk,
  finalizeSalesRecapDataThunk,
  deleteSalesRecapScheduleThunk,
};

export function selectSalesRecap(state) {
  return state?.salesRecap;
}

export default salesRecapSlice.reducer;
